import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { AuthShell } from '@/components/auth-shell'
import { BtnPrimary, Field, TextInput } from '@/components/field'
import { apiMutate, ApiError } from '@/lib/api'

type Step = 'credentials' | 'totp' | 'enroll' | 'password'

type LoginResult =
  | { status: 'ENROLL_REQUIRED'; challengeToken: string }
  | { status: 'TOTP_REQUIRED'; challengeToken: string }
  | {
      status: 'AUTHENTICATED'
      user: { mustChangePassword: boolean }
      backupCodes?: string[]
    }

type EnrollStart = { otpauthUrl: string; secret: string }

function redirectAfterAuth() {
  const params = new URLSearchParams(window.location.search)
  const target = params.get('redirect') || '/'
  window.location.href = target.startsWith('/') ? target : '/'
}

export function ConnexionPage() {
  const [step, setStep] = useState<Step>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [challengeToken, setChallengeToken] = useState('')
  const [code, setCode] = useState('')
  const [otpauthUrl, setOtpauthUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [mustChangeAfterBackup, setMustChangeAfterBackup] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const login = useMutation({
    mutationFn: () =>
      apiMutate<LoginResult>('/staff/auth/login', 'POST', {
        email: email.trim(),
        password,
      }),
    onSuccess: async (res) => {
      setError(null)
      if (res.status === 'ENROLL_REQUIRED') {
        setChallengeToken(res.challengeToken)
        const start = await apiMutate<EnrollStart>('/staff/auth/totp/enroll/start', 'POST', {
          challengeToken: res.challengeToken,
        })
        setOtpauthUrl(start.otpauthUrl)
        setSecret(start.secret)
        setStep('enroll')
        return
      }
      if (res.status === 'TOTP_REQUIRED') {
        setChallengeToken(res.challengeToken)
        setStep('totp')
        return
      }
      if (res.user.mustChangePassword) {
        setStep('password')
        return
      }
      redirectAfterAuth()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  const totp = useMutation({
    mutationFn: () =>
      apiMutate<LoginResult>('/staff/auth/totp', 'POST', {
        challengeToken,
        code: code.trim(),
      }),
    onSuccess: (res) => {
      setError(null)
      if (res.status === 'AUTHENTICATED' && res.user.mustChangePassword) {
        setStep('password')
        return
      }
      redirectAfterAuth()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  const enrollConfirm = useMutation({
    mutationFn: () =>
      apiMutate<LoginResult & { backupCodes?: string[] }>('/staff/auth/totp/enroll/confirm', 'POST', {
        challengeToken,
        code: code.trim(),
      }),
    onSuccess: (res) => {
      setError(null)
      if (res.backupCodes?.length) {
        setBackupCodes(res.backupCodes)
        setMustChangeAfterBackup(
          res.status === 'AUTHENTICATED' && res.user.mustChangePassword,
        )
        return
      }
      if (res.status === 'AUTHENTICATED' && res.user.mustChangePassword) {
        setStep('password')
        return
      }
      redirectAfterAuth()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  const changePassword = useMutation({
    mutationFn: () =>
      apiMutate('/staff/auth/password', 'POST', {
        currentPassword,
        newPassword,
      }),
    onSuccess: () => {
      setError(null)
      redirectAfterAuth()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accès réservé au personnel. Session inactive après 20 minutes."
      footer={
        step === 'credentials' ? (
          <Link to="/connexion/oublie" className="text-rouge underline">
            Mot de passe oublié ?
          </Link>
        ) : null
      }
    >
      {error ? (
        <p className="mb-4 rounded-lg bg-rouge/10 px-3 py-2 text-sm text-rouge" role="alert">
          {error}
        </p>
      ) : null}

      {backupCodes ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            Conservez ces codes de secours (affichés une seule fois). Chaque code ne peut être
            utilisé qu’une fois.
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
            {backupCodes.map((c) => (
              <li key={c} className="rounded bg-page px-2 py-1">
                {c}
              </li>
            ))}
          </ul>
          <BtnPrimary
            type="button"
            onClick={() => {
              setBackupCodes(null)
              if (mustChangeAfterBackup) {
                setStep('password')
                return
              }
              redirectAfterAuth()
            }}
          >
            Continuer
          </BtnPrimary>
        </div>
      ) : null}

      {!backupCodes && step === 'credentials' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            login.mutate()
          }}
        >
          <Field label="E-mail">
            <TextInput
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Mot de passe">
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          <BtnPrimary type="submit" disabled={login.isPending}>
            {login.isPending ? 'Connexion…' : 'Continuer'}
          </BtnPrimary>
        </form>
      ) : null}

      {!backupCodes && step === 'totp' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            totp.mutate()
          }}
        >
          <Field label="Code d’authentification" hint="Application TOTP ou code de secours">
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Field>
          <BtnPrimary type="submit" disabled={totp.isPending}>
            {totp.isPending ? 'Vérification…' : 'Valider'}
          </BtnPrimary>
        </form>
      ) : null}

      {!backupCodes && step === 'enroll' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            enrollConfirm.mutate()
          }}
        >
          <p className="text-sm text-second-admin">
            Ouvrez ce lien dans votre application d’authentification, ou saisissez le secret
            manuellement.
          </p>
          <a href={otpauthUrl} className="break-all text-sm text-rouge underline" rel="noreferrer">
            {otpauthUrl}
          </a>
          <Field label="Secret (manuel)">
            <TextInput readOnly value={secret} className="font-mono text-xs" />
          </Field>
          <Field label="Code à 6 chiffres">
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </Field>
          <BtnPrimary type="submit" disabled={enrollConfirm.isPending}>
            {enrollConfirm.isPending ? 'Enrôlement…' : 'Confirmer TOTP'}
          </BtnPrimary>
        </form>
      ) : null}

      {!backupCodes && step === 'password' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            changePassword.mutate()
          }}
        >
          <p className="text-sm text-second-admin">Vous devez changer votre mot de passe.</p>
          <Field label="Mot de passe actuel">
            <TextInput
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </Field>
          <Field label="Nouveau mot de passe" hint="Au moins 10 caractères">
            <TextInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={10}
              required
            />
          </Field>
          <BtnPrimary type="submit" disabled={changePassword.isPending}>
            {changePassword.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BtnPrimary>
        </form>
      ) : null}
    </AuthShell>
  )
}
